import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Card, Modal, Input, Select, Textarea, Empty, Spinner, useToast } from '../components/shared/UI';
import codes from '../data/einvoiceCodes.json';
import { useT } from '../i18n';
import api from '../utils/api';

const E_INVOICE_FIELDS = { tin: '', idType: '', idValue: '', sstNo: '', postcode: '', stateCode: '', countryCode: 'MYS' };
const GENERAL_TIN = { public: 'EI00000000010', foreign: 'EI00000000020' };
const ID_TYPES = ['BRN', 'NRIC', 'PASSPORT', 'ARMY'];
const empty = { name: '', company: '', email: '', phone: '', address: '', city: '', notes: '', ...E_INVOICE_FIELDS };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);
  const [showEinv, setShowEinv] = useState(false);
  const [tinCheck, setTinCheck] = useState(null); // null | 'checking' | 'valid' | 'invalid' | error text
  const { show, ToastContainer } = useToast();
  const { t } = useT();
  const navigate = useNavigate();

  const load =() => api.get('/clients').then(r => setClients(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew  = ()  => { setEditing(null); setForm(empty); setShowEinv(false); setTinCheck(null); setModal(true); };
  const openEdit = (c) => { setEditing(c._id); setForm({ ...empty, name: c.name, company: c.company, email: c.email, phone: c.phone, address: c.address, city: c.city, notes: c.notes,
    ...Object.fromEntries(Object.keys(E_INVOICE_FIELDS).map(k => [k, c[k] ?? E_INVOICE_FIELDS[k]])) });
    setShowEinv(!!c.tin); setTinCheck(null); setModal(true); };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const checkTin = async () => {
    setTinCheck('checking');
    try {
      const { data } = await api.post('/einvoice/validate-tin', { tin: form.tin, idType: form.idType, idValue: form.idValue });
      setTinCheck(data.result);
    } catch (err) { setTinCheck(err.response?.data?.message || t('einv.failed')); }
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { const { data } = await api.put(`/clients/${editing}`, form); setClients(cs => cs.map(c => c._id === editing ? data : c)); }
      else         { const { data } = await api.post('/clients', form); setClients(cs => [...cs, data]); }
      show(editing ? t('clients.updated') : t('clients.added'));
      setModal(false);
    } catch { show(t('editor.errorSaving'), 'error'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm(t('clients.confirmDelete'))) return;
    await api.delete(`/clients/${id}`);
    setClients(cs => cs.filter(c => c._id !== id));
    show(t('clients.deleted'));
  };

  return (
    <div>
      <ToastContainer />
      <PageHeader title={t('clients.title')} subtitle={t('clients.count', { n: clients.length })}
        actions={<Btn variant="primary" onClick={openNew}>+ {t('clients.addClient')}</Btn>} />

      {loading ? <Spinner /> : clients.length === 0
        ? <Empty icon="👥" message={t('clients.empty')}
            action={<Btn variant="primary" onClick={openNew}>{t('clients.addClient')}</Btn>} />
        : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {clients.map(c => (
              <Card key={c._id} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: 'var(--light-blue)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 700, color: 'var(--heading)', flexShrink: 0
                  }}>
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</p>
                    {c.company && <p style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company}</p>}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 14, borderTop: '1px solid var(--border-light)', paddingTop: 12 }}>
                  {c.email && <div>✉ {c.email}</div>}
                  {c.phone && <div>📞 {c.phone}</div>}
                  {c.city  && <div>📍 {c.city}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                  <Btn size="sm" variant="ghost" onClick={() => navigate(`/clients/${c._id}/statement`)} style={{ flex: 1, justifyContent: 'center' }}>📊 {t('statement.button')}</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(c)} style={{ flex: 1, justifyContent: 'center' }}>{t('common.edit')}</Btn>
                  <Btn size="sm" variant="danger" onClick={() => del(c._id)}>{t('common.delete')}</Btn>
                </div>
              </Card>
            ))}
          </div>
        )
      }

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? t('clients.editClient') : t('clients.addClient')}>
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label={t('clients.fullName')} value={form.name} onChange={set('name')} required placeholder="Siti Aminah" />
            <Input label={t('clients.company')} value={form.company} onChange={set('company')} placeholder="ABC Sdn Bhd" />
            <Input label={t('clients.email')} type="email" value={form.email} onChange={set('email')} placeholder="siti@abc.com" />
            <Input label={t('clients.phone')} value={form.phone} onChange={set('phone')} placeholder="012-345 6789" />
            <Input label={t('clients.address')} value={form.address} onChange={set('address')} placeholder="No 1, Jalan Utama" />
            <Input label={t('clients.city')} value={form.city} onChange={set('city')} placeholder="Kuala Lumpur" />
          </div>
          <Textarea label={t('clients.notes')} value={form.notes} onChange={set('notes')} placeholder={t('clients.notesPh')} rows={2} />

          {/* LHDN e-Invoice buyer details — collapsed unless in use */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <button type="button" onClick={() => setShowEinv(v => !v)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--hover-bg)', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--heading)' }}>
              <span>🏛 {t('einv.clientSection')}</span><span>{showEinv ? '▲' : '▼'}</span>
            </button>
            {showEinv && (
              <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('einv.clientSectionHelp')}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label={t('einv.buyerTin')} value={form.tin} onChange={set('tin')} maxLength={14} placeholder="C10293847560" dir="ltr" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'flex-end' }}>
                    <Btn size="sm" variant="ghost" type="button" onClick={() => setForm(f => ({ ...f, tin: GENERAL_TIN.public, countryCode: 'MYS' }))}>{t('einv.generalPublic')}</Btn>
                    <Btn size="sm" variant="ghost" type="button" onClick={() => setForm(f => ({ ...f, tin: GENERAL_TIN.foreign, stateCode: '' }))}>{t('einv.generalForeign')}</Btn>
                  </div>
                  <Select label={t('einv.idType')} value={form.idType} onChange={set('idType')}>
                    <option value="">—</option>
                    {ID_TYPES.map(x => <option key={x} value={x}>{t(`einv.id_${x}`)}</option>)}
                  </Select>
                  <Input label={t('einv.idValue')} value={form.idValue} onChange={set('idValue')} maxLength={20} dir="ltr" />
                  <Input label={t('einv.postcode')} value={form.postcode} onChange={set('postcode')} maxLength={10} dir="ltr" />
                  <Select label={t('einv.state')} value={form.stateCode} onChange={set('stateCode')}>
                    <option value="">{t('einv.chooseState')}</option>
                    {codes.states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </Select>
                  <Input label={t('einv.country')} value={form.countryCode} onChange={e => setForm(f => ({ ...f, countryCode: e.target.value.toUpperCase() }))} maxLength={3} placeholder="MYS" dir="ltr" />
                  <Input label={t('einv.sstNo')} value={form.sstNo} onChange={set('sstNo')} maxLength={35} dir="ltr" />
                </div>
                {form.tin && form.idType && form.idValue && !Object.values(GENERAL_TIN).includes(form.tin) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <Btn size="sm" variant="outline" type="button" onClick={checkTin} disabled={tinCheck === 'checking'}>{tinCheck === 'checking' ? t('einv.testing') : t('einv.checkTin')}</Btn>
                    {tinCheck && tinCheck !== 'checking' && (
                      <span style={{ fontSize: 12.5, color: tinCheck === 'valid' ? 'var(--success)' : 'var(--danger)' }}>
                        {tinCheck === 'valid' ? `✓ ${t('einv.tinValid')}` : tinCheck === 'invalid' ? `✕ ${t('einv.tinInvalid')}` : tinCheck}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setModal(false)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" type="submit" disabled={saving}>{saving ? t('common.saving') : editing ? t('editor.saveChanges') : t('clients.addClient')}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
